import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { hasTfPendingTag } from "@/lib/tf-pending";
import { isEventExpired } from "@/lib/event-visibility";

// ---------------------------------------------------------------------------
// DTOs (client-safe)
// ---------------------------------------------------------------------------
export type PracticalInfo = { title: string; body: string };

export type DistrictMapPoint = {
  kind: "entrance" | "parking";
  label: string;
  note?: string | null;
  lat: number;
  lng: number;
};

export type DistrictSectionKey = "restaurants" | "lunch" | "menus" | "events" | "do" | "map" | "info";

export const DISTRICT_SECTION_KEYS: DistrictSectionKey[] = [
  "restaurants",
  "lunch",
  "menus",
  "do",
  "events",
  "map",
  "info",
];

export type DistrictPageConfig = {
  sections: {
    lunch: boolean;
    menus: boolean;
    events: boolean;
    map: boolean;
    info: boolean;
    restaurants: boolean;
    do: boolean;
  };
  /** Display order of the overview sections on the public hub page. */
  order: DistrictSectionKey[];
  lunch_ids: string[];
  event_ids: string[];
  restaurant_ids: string[];
  do_ids: string[];
};

export const DEFAULT_DISTRICT_PAGE: DistrictPageConfig = {
  sections: { lunch: true, menus: true, events: true, map: true, info: true, restaurants: true, do: true },
  order: [...DISTRICT_SECTION_KEYS],
  lunch_ids: [],
  event_ids: [],
  restaurant_ids: [],
  do_ids: [],
};


/** Keeps a stored order valid: known keys only, no dupes, missing keys appended. */
export function normalizeSectionOrder(value: unknown): DistrictSectionKey[] {
  const list = Array.isArray(value) ? value : [];
  const seen = new Set<DistrictSectionKey>();
  const out: DistrictSectionKey[] = [];
  for (const item of list) {
    if (typeof item !== "string") continue;
    const key = item as DistrictSectionKey;
    if (!DISTRICT_SECTION_KEYS.includes(key) || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  for (const key of DISTRICT_SECTION_KEYS) if (!seen.has(key)) out.push(key);
  return out;
}

function parsePageConfig(mapConfig: unknown): DistrictPageConfig {
  const raw = (mapConfig && typeof mapConfig === "object" ? (mapConfig as { page?: unknown }).page : null) as
    | Record<string, unknown>
    | null
    | undefined;
  const sectionsIn = (raw?.sections ?? {}) as Record<string, unknown>;
  const ids = (value: unknown) =>
    Array.isArray(value) ? value.filter((id): id is string => typeof id === "string" && id.length > 0) : [];
  return {
    sections: {
      lunch: sectionsIn.lunch !== false,
      menus: sectionsIn.menus !== false,

      events: sectionsIn.events !== false,
      map: sectionsIn.map !== false,
      info: sectionsIn.info !== false,
      restaurants: sectionsIn.restaurants !== false,
      do: sectionsIn.do !== false,
    },
    order: normalizeSectionOrder(raw?.order),
    lunch_ids: ids(raw?.lunch_ids),
    event_ids: ids(raw?.event_ids),
    restaurant_ids: ids(raw?.restaurant_ids),
    do_ids: ids(raw?.do_ids),
  };
}


export type DistrictDTO = {
  id: string;
  slug: string;
  name: string;
  city_slug: string | null;
  tagline: string | null;
  description: string | null;
  hero_image_url: string | null;
  logo_url: string | null;
  official_partner: boolean;
  practical_info: { title: string; body: string }[];
  map_points: DistrictMapPoint[];
  page: DistrictPageConfig;
  lat: number | null;
  lng: number | null;
  address: string | null;
  website: string | null;
  seo_title: string | null;
  seo_description: string | null;
  published: boolean;
};


export type DistrictMenuDishDTO = {
  id: string;
  section: string | null;
  name: string;
  description: string | null;
  price: number | null;
  currency: string;
  dietary_tags: string[];
  image_url: string | null;
};

export type DistrictMenuDTO = {
  place_id: string;
  slug: string;
  name: string;
  image: string | null;
  dishes: DistrictMenuDishDTO[];
};

export type DistrictVenueDTO = {

  id: string;
  slug: string;
  name: string;
  section: string;
  category: string | null;
  image: string | null;
  address: string | null;
  area: string | null;
  lat: number | null;
  lng: number | null;
  tags: string[];
  phone: string | null;
  email: string | null;
  link: string | null;
  featured: boolean;
  sort_order: number;
  // hours + menu
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  opening_hours: any | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  kitchen_hours: any | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lunch_hours: any | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  special_hours: any[];
  temporarily_closed: boolean;
  temporarily_closed_note: string | null;
  schedule_info: string | null;
  menu_pdf_url: string | null;
  menu_image_url: string | null;
  menu_external_url: string | null;
  menu_updated_at: string | null;
  info_updated_at: string | null;
  /** True when the venue has dishes entered in the admin menu editor. */
  has_dish_menu?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  date_label?: string | null;
  price?: string | null;
  buy_ticket_url?: string | null;
};

const VENUE_COLUMNS =
  "id,slug,name,section,category,image_url,address,area,lat,lng,tags,phone,email,link," +
  "opening_hours,kitchen_hours,lunch_hours,special_hours,temporarily_closed,temporarily_closed_note,schedule_info," +
  "menu_pdf_url,menu_image_url,menu_external_url,menu_updated_at,info_updated_at,starts_at,ends_at,date_label,price,buy_ticket_url,published";

/* eslint-disable @typescript-eslint/no-explicit-any */
function toVenue(p: any, link?: { featured?: boolean; sort_order?: number }): DistrictVenueDTO {
  return {
    id: p.id,
    slug: p.slug,
    name: p.name,
    section: p.section,
    category: p.category ?? null,
    image: p.image_url ?? null,
    address: p.address ?? null,
    area: p.area ?? null,
    lat: p.lat ?? null,
    lng: p.lng ?? null,
    tags: (p.tags ?? []) as string[],
    phone: p.phone ?? null,
    email: p.email ?? null,
    link: p.link ?? null,
    featured: link?.featured ?? false,
    sort_order: link?.sort_order ?? 0,
    opening_hours: p.opening_hours ?? null,
    kitchen_hours: p.kitchen_hours ?? null,
    lunch_hours: p.lunch_hours ?? null,
    special_hours: Array.isArray(p.special_hours) ? p.special_hours : [],
    temporarily_closed: !!p.temporarily_closed,
    temporarily_closed_note: p.temporarily_closed_note ?? null,
    schedule_info: p.schedule_info ?? null,
    menu_pdf_url: p.menu_pdf_url ?? null,
    menu_image_url: p.menu_image_url ?? null,
    menu_external_url: p.menu_external_url ?? null,
    menu_updated_at: p.menu_updated_at ?? null,
    info_updated_at: p.info_updated_at ?? null,
    starts_at: p.starts_at ?? null,
    ends_at: p.ends_at ?? null,
    date_label: p.date_label ?? null,
    price: p.price ?? null,
    buy_ticket_url: p.buy_ticket_url ?? null,
  };
}

function toDistrict(d: any): DistrictDTO {
  return {
    id: d.id,
    slug: d.slug,
    name: d.name,
    city_slug: d.city_slug ?? null,
    tagline: d.tagline ?? null,
    description: d.description ?? null,
    hero_image_url: d.hero_image_url ?? null,
    logo_url: d.logo_url ?? null,
    official_partner: !!d.official_partner,
    practical_info: Array.isArray(d.practical_info) ? d.practical_info : [],
    page: parsePageConfig(d.map_config),
    map_points: Array.isArray(d.map_config?.points)
      ? (d.map_config.points as any[])
          .filter((p) => p && typeof p.lat === "number" && typeof p.lng === "number")
          .map((p) => ({
            kind: p.kind === "parking" ? "parking" : "entrance",
            label: String(p.label ?? (p.kind === "parking" ? "Parking" : "Entrance")),
            note: p.note ?? null,
            lat: p.lat as number,
            lng: p.lng as number,
          }))
      : [],

    lat: d.lat ?? null,
    lng: d.lng ?? null,
    address: d.address ?? null,
    website: d.website ?? null,
    seo_title: d.seo_title ?? null,
    seo_description: d.seo_description ?? null,
    published: !!d.published,
  };
}

// ---------------------------------------------------------------------------
// Public reads (no auth — safe for SSR / prerender)
// ---------------------------------------------------------------------------
export const listPublicDistricts = createServerFn({ method: "GET" }).handler(async () => {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data, error } = await supabaseAdmin
    .from("districts")
    .select("id,slug,name,city_slug,tagline,description,hero_image_url,logo_url,official_partner,published")
    .eq("published", true)
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  const rows = data ?? [];
  const fallback = new Map<string, string>();
  const venueCounts = new Map<string, number>();

  if (rows.length > 0) {
    const districtIds = rows.map((d: any) => d.id);
    const { data: links } = await supabaseAdmin
      .from("district_venues")
      .select("district_id,place_id,sort_order")
      .in("district_id", districtIds)
      .order("sort_order", { ascending: true });
    const placeIds = [...new Set((links ?? []).map((l: any) => l.place_id))];
    const published = new Map<string, string | null>();
    if (placeIds.length > 0) {
      const { data: places } = await supabaseAdmin
        .from("places")
        .select("id,image_url,tags")
        .in("id", placeIds)
        .eq("published", true);
      for (const p of places ?? []) {
        if (hasTfPendingTag((p as any).tags)) continue;
        published.set((p as any).id, (p as any).image_url ?? null);
      }
    }
    for (const l of links ?? []) {
      const placeId = (l as any).place_id as string;
      if (!published.has(placeId)) continue;
      const districtId = (l as any).district_id as string;
      venueCounts.set(districtId, (venueCounts.get(districtId) ?? 0) + 1);
      const img = published.get(placeId);
      if (img && !fallback.has(districtId)) fallback.set(districtId, img);
    }
  }

  return rows.map((d: any) => ({
    slug: d.slug as string,
    name: d.name as string,
    city_slug: (d.city_slug ?? null) as string | null,
    tagline: (d.tagline ?? null) as string | null,
    hero_image_url: (d.hero_image_url ?? fallback.get(d.id) ?? null) as string | null,
    logo_url: (d.logo_url ?? null) as string | null,
    official_partner: !!d.official_partner,
    venue_count: venueCounts.get(d.id) ?? 0,
  }));
});


export const getPublicDistrict = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(80) }).parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const slugsToTry =
      data.slug === "train-factory" || data.slug === "konepaja"
        ? ["train-factory", "konepaja"]
        : [data.slug];

    let district: any = null;
    const aliasRows: any[] = [];
    for (const slug of slugsToTry) {
      const { data: row, error } = await supabaseAdmin
        .from("districts")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (row) {
        aliasRows.push(row);
        if (!district) district = row;
      }
    }
    if (!district) return null;

    const districtIds = [...new Set(aliasRows.map((d) => d.id))];
    const { data: links, error: linkErr } = await supabaseAdmin
      .from("district_venues")
      .select("place_id,featured,sort_order")
      .in("district_id", districtIds)
      .order("sort_order", { ascending: true });
    if (linkErr) throw new Error(linkErr.message);

    const page = parsePageConfig(district.map_config);
    const seenPlaces = new Set<string>();
    const uniqueLinks = (links ?? []).filter((l: any) => {
      if (seenPlaces.has(l.place_id)) return false;
      seenPlaces.add(l.place_id);
      return true;
    });
    const ids = [
      ...new Set([
        ...uniqueLinks.map((l: any) => l.place_id as string),
        ...page.lunch_ids,
        ...page.event_ids,
        ...page.restaurant_ids,
        ...page.do_ids,
      ]),
    ];
    let venues: DistrictVenueDTO[] = [];
    const byPlace = new Map<string, DistrictVenueDTO>();
    if (ids.length > 0) {
      const { data: places, error: placesErr } = await supabaseAdmin
        .from("places")
        .select(VENUE_COLUMNS)
        .in("id", ids)
        .eq("published", true);
      if (placesErr) throw new Error(placesErr.message);
      const byId = new Map(uniqueLinks.map((l: any) => [l.place_id, l]));
      venues = (places ?? [])
        .filter((p: any) => !hasTfPendingTag(p.tags))
        .map((p: any) => toVenue(p, byId.get(p.id)))
        .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
      for (const v of venues) byPlace.set(v.id, v);
    }

    const now = Date.now();
    const isStillOn = (v: DistrictVenueDTO) => {
      const start = v.starts_at ? new Date(v.starts_at).getTime() : NaN;
      if (Number.isFinite(start) && start > now) return false;
      return !isEventExpired({ startsAt: v.starts_at, endsAt: v.ends_at }, now);
    };
    const autoEvents = venues
      .filter((v) => v.section === "happening")
      .filter(isStillOn)
      .sort((a, b) => new Date(a.starts_at ?? 0).getTime() - new Date(b.starts_at ?? 0).getTime());
    const events =
      page.event_ids.length > 0
        ? page.event_ids
            .map((id) => byPlace.get(id))
            .filter((v): v is DistrictVenueDTO => !!v)
            .filter(isStillOn)
        : autoEvents;

    const autoLunch = venues
      .filter((v) => v.section !== "happening")
      .filter((v) => {
        const hours = v.lunch_hours;
        if (!hours || typeof hours !== "object") return false;
        return Object.values(hours as Record<string, { closed?: boolean; open?: string; close?: string }>).some(
          (d) => d && !d.closed && !!d.open && !!d.close,
        );
      })
      .slice(0, 12);
    const lunch =
      page.lunch_ids.length > 0
        ? page.lunch_ids.map((id) => byPlace.get(id)).filter((v): v is DistrictVenueDTO => !!v)
        : autoLunch;

    const hub = toDistrict(district);
    if (!hub.hero_image_url) {
      hub.hero_image_url = venues.find((v) => v.image)?.image ?? null;
    }

    // Dish-level menus entered in the admin menu editor, grouped per venue.
    const menuVenues = venues.filter((v) => v.section !== "happening");
    const menus: DistrictMenuDTO[] = [];
    if (menuVenues.length > 0) {
      const { data: dishRows } = await supabaseAdmin
        .from("place_menu_items")
        .select("id,place_id,section,name,description,price,currency,dietary_tags,image_url,sort_order,is_active")
        .in(
          "place_id",
          menuVenues.map((v) => v.id),
        )
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      const byVenue = new Map<string, any[]>();
      for (const row of (dishRows ?? []) as any[]) {
        const list = byVenue.get(row.place_id);
        if (list) list.push(row);
        else byVenue.set(row.place_id, [row]);
      }
      for (const v of menuVenues) {
        const rows = byVenue.get(v.id);
        if (!rows || rows.length === 0) continue;
        menus.push({
          place_id: v.id,
          slug: v.slug,
          name: v.name,
          image: v.image,
          dishes: rows.map((r) => ({
            id: String(r.id),
            section: (r.section as string) || null,
            name: (r.name as string) ?? "",
            description: (r.description as string) || null,
            price: r.price === null || r.price === undefined ? null : Number(r.price),
            currency: (r.currency as string) || "EUR",
            dietary_tags: Array.isArray(r.dietary_tags) ? (r.dietary_tags as string[]) : [],
            image_url: (r.image_url as string) || null,
          })),
        });
      }
    }

    const dishMenuIds = new Set(menus.map((m) => m.place_id));
    for (const v of venues) v.has_dish_menu = dishMenuIds.has(v.id);

    return {
      district: hub,
      venues: venues.filter((v) => v.section !== "happening"),
      events,
      lunch,
      menus,

    };
  });


/** Used by venue detail pages to render the "part of …" cross-link. */
export const getDistrictForPlace = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(160) }).parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: place } = await supabaseAdmin
      .from("places")
      .select("id,published,tags")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!place || !(place as any).published || hasTfPendingTag((place as any).tags)) return null;
    const { data: link } = await supabaseAdmin
      .from("district_venues")
      .select("districts(slug,name,published)")
      .eq("place_id", (place as any).id)
      .maybeSingle();
    const d = (link as any)?.districts;
    if (!d || !d.published) return null;
    return { slug: d.slug as string, name: d.name as string };
  });

// ---------------------------------------------------------------------------
// Authenticated: venue self-service
// ---------------------------------------------------------------------------
const dayShape = z
  .object({
    open: z.string().max(5).nullish(),
    close: z.string().max(5).nullish(),
    closed: z.boolean().nullish(),
  })
  .passthrough();

const weeklyShape = z.record(z.string().max(12), dayShape).nullable().optional();

const specialShape = z
  .array(
    z.object({
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      closed: z.boolean().optional(),
      open: z.string().max(5).nullish(),
      close: z.string().max(5).nullish(),
      note: z.string().max(200).nullish(),
    }),
  )
  .max(120)
  .optional();

/** Venues the signed-in user is allowed to manage, with district context. */
export const listManageableVenues = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: memberships } = await supabase
      .from("district_admins")
      .select("district_id,level,place_id,districts(slug,name)")
      .eq("user_id", userId);

    const { data: isAdmin } = await supabase.rpc("current_user_is_admin");
    const { data: isSuper } = await supabase.rpc("current_user_is_super_admin");
    const siteAdmin = !!isAdmin || !!isSuper;

    const masterDistricts = (memberships ?? [])
      .filter((m: any) => m.level === "master")
      .map((m: any) => m.district_id as string);
    const directPlaceIds = (memberships ?? [])
      .filter((m: any) => m.place_id)
      .map((m: any) => m.place_id as string);

    const placeIds = new Set<string>(directPlaceIds);

    if (masterDistricts.length > 0) {
      const { data: dv } = await supabase
        .from("district_venues")
        .select("place_id")
        .in("district_id", masterDistricts);
      for (const row of dv ?? []) placeIds.add((row as any).place_id);
    }

    // Partner users manage the venues linked to their CRM client.
    const { data: partnerLinks } = await supabase
      .from("partner_users")
      .select("client_id")
      .eq("user_id", userId);
    const clientIds = (partnerLinks ?? []).map((p: any) => p.client_id);
    if (clientIds.length > 0) {
      const { data: pp } = await supabase.from("places").select("id").in("crm_client_id", clientIds);
      for (const row of pp ?? []) placeIds.add((row as any).id);
    }

    if (placeIds.size === 0 && !siteAdmin) {
      return { venues: [] as DistrictVenueDTO[], isMaster: masterDistricts.length > 0, siteAdmin: false };
    }

    let query = supabase.from("places").select(VENUE_COLUMNS).order("name", { ascending: true });
    if (!siteAdmin) query = query.in("id", Array.from(placeIds));
    else if (placeIds.size > 0) query = query.in("id", Array.from(placeIds));
    else query = query.limit(0);

    const { data: places, error } = await query;
    if (error) throw new Error(error.message);

    return {
      venues: (places ?? []).map((p: any) => toVenue(p)),
      isMaster: masterDistricts.length > 0 || siteAdmin,
      siteAdmin,
    };
  });

export const updateVenueInfo = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        placeId: z.string().uuid(),
        opening_hours: weeklyShape,
        kitchen_hours: weeklyShape,
        lunch_hours: weeklyShape,
        special_hours: specialShape,
        temporarily_closed: z.boolean().optional(),
        temporarily_closed_note: z.string().max(300).nullish(),
        menu_pdf_url: z.string().url().max(1000).nullish(),
        menu_image_url: z.string().url().max(1000).nullish(),
        menu_external_url: z.string().url().max(1000).nullish(),
        phone: z.string().max(60).nullish(),
        email: z.string().max(200).nullish(),
        link: z.string().max(1000).nullish(),
        description: z.string().max(4000).nullish(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { placeId, ...rest } = data;

    const { data: allowed, error: permErr } = await supabase.rpc("can_manage_venue", {
      _user_id: userId,
      _place_id: placeId,
    });
    if (permErr) throw new Error(permErr.message);
    if (!allowed) throw new Error("Forbidden: you cannot manage this venue.");

    const patch: Record<string, unknown> = { info_updated_at: new Date().toISOString(), info_updated_by: userId };
    for (const [k, v] of Object.entries(rest)) {
      if (v !== undefined) patch[k] = v;
    }
    const menuTouched =
      rest.menu_pdf_url !== undefined || rest.menu_image_url !== undefined || rest.menu_external_url !== undefined;
    if (menuTouched) patch["menu_updated_at"] = new Date().toISOString();

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("places").update(patch as never).eq("id", placeId);
    if (error) throw new Error(error.message);
    return { ok: true, updatedAt: patch["info_updated_at"] as string };
  });

/** One-tap "update today's hours". */
export const updateTodayHours = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        placeId: z.string().uuid(),
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        closed: z.boolean(),
        open: z.string().max(5).nullish(),
        close: z.string().max(5).nullish(),
        note: z.string().max(200).nullish(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: allowed } = await supabase.rpc("can_manage_venue", {
      _user_id: userId,
      _place_id: data.placeId,
    });
    if (!allowed) throw new Error("Forbidden: you cannot manage this venue.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error: readErr } = await supabaseAdmin
      .from("places")
      .select("special_hours")
      .eq("id", data.placeId)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);

    const existing = Array.isArray((row as any)?.special_hours) ? (row as any).special_hours : [];
    const next = existing.filter((s: any) => s?.date !== data.date);
    next.push({
      date: data.date,
      closed: data.closed,
      open: data.closed ? null : (data.open ?? null),
      close: data.closed ? null : (data.close ?? null),
      note: data.note ?? null,
    });

    const { error } = await supabaseAdmin
      .from("places")
      .update({
        special_hours: next,
        info_updated_at: new Date().toISOString(),
        info_updated_by: userId,
      })
      .eq("id", data.placeId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ---------------------------------------------------------------------------
// Authenticated: district master admin
// ---------------------------------------------------------------------------
async function requireMaster(supabase: any, userId: string, districtId: string) {
  const { data, error } = await supabase.rpc("is_district_master", {
    _user_id: userId,
    _district_id: districtId,
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden: district master access required.");
}

export const getDistrictForManage = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(80) }).parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: district, error } = await supabaseAdmin
      .from("districts")
      .select("*")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!district) return null;
    await requireMaster(supabase, userId, (district as any).id);

    const { data: links } = await supabaseAdmin
      .from("district_venues")
      .select("place_id,featured,sort_order")
      .eq("district_id", (district as any).id)
      .order("sort_order", { ascending: true });

    const ids = (links ?? []).map((l: any) => l.place_id);
    let venues: DistrictVenueDTO[] = [];
    if (ids.length > 0) {
      const { data: places } = await supabaseAdmin.from("places").select(VENUE_COLUMNS).in("id", ids);
      const byId = new Map((links ?? []).map((l: any) => [l.place_id, l]));
      venues = (places ?? [])
        .map((p: any) => toVenue(p, byId.get(p.id)))
        .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    }

    return { district: toDistrict(district), venues };
  });

export const updateDistrict = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        districtId: z.string().uuid(),
        name: z.string().min(1).max(160).optional(),
        tagline: z.string().max(300).nullish(),
        description: z.string().max(6000).nullish(),
        hero_image_url: z.string().max(1000).nullish(),
        address: z.string().max(300).nullish(),
        website: z.string().max(500).nullish(),
        official_partner: z.boolean().optional(),
        published: z.boolean().optional(),
        practical_info: z
          .array(z.object({ title: z.string().max(120), body: z.string().max(1200) }))
          .max(20)
          .optional(),
        map_points: z
          .array(
            z.object({
              kind: z.enum(["entrance", "parking"]),
              label: z.string().max(120),
              note: z.string().max(300).nullish(),
              lat: z.number().min(-90).max(90),
              lng: z.number().min(-180).max(180),
            }),
          )
          .max(50)
          .optional(),
      })

      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { districtId, map_points, ...rest } = data;
    await requireMaster(supabase, userId, districtId);
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(rest)) if (v !== undefined) patch[k] = v;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    if (map_points !== undefined) {
      const { data: existing } = await supabaseAdmin
        .from("districts")
        .select("map_config")
        .eq("id", districtId)
        .maybeSingle();
      const base = ((existing as any)?.map_config ?? {}) as Record<string, unknown>;
      patch["map_config"] = { ...base, points: map_points };
    }
    const { error } = await supabaseAdmin.from("districts").update(patch as never).eq("id", districtId);

    if (error) throw new Error(error.message);
    return { ok: true };
  });

/** Slugs that make up one district (Train Factory keeps its Finnish alias in sync). */
function districtSlugs(slug?: string | null): string[] {
  const s = slug ?? "train-factory";
  return s === "train-factory" || s === "konepaja" ? ["train-factory", "konepaja"] : [s];
}

/** Business admin: replace a district hub banner / tagline / section picks. */
export const updateTrainFactoryPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        slug: z.string().min(1).max(80).optional(),
        hero_image_url: z.string().max(2000).nullish(),
        tagline: z.string().max(300).nullish(),
        page: z
          .object({
            sections: z
              .object({
                lunch: z.boolean().optional(),
                menus: z.boolean().optional(),

                events: z.boolean().optional(),
                map: z.boolean().optional(),
                info: z.boolean().optional(),
                restaurants: z.boolean().optional(),
                do: z.boolean().optional(),
              })
              .optional(),
            lunch_ids: z.array(z.string().uuid()).max(40).optional(),
            event_ids: z.array(z.string().uuid()).max(40).optional(),
            restaurant_ids: z.array(z.string().uuid()).max(40).optional(),
            do_ids: z.array(z.string().uuid()).max(40).optional(),
            order: z
              .array(z.enum(["restaurants", "lunch", "menus", "events", "do", "map", "info"]))
              .max(12)
              .optional(),

          })
          .optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { assertIsAdmin } = await import("@/lib/assert-admin.server");
    await assertIsAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error: readErr } = await supabaseAdmin
      .from("districts")
      .select("id,map_config")
      .in("slug", districtSlugs(data.slug));
    if (readErr) throw new Error(readErr.message);
    for (const row of rows ?? []) {
      const cfg =
        row.map_config && typeof row.map_config === "object" && !Array.isArray(row.map_config)
          ? (row.map_config as Record<string, unknown>)
          : {};
      const prev = parsePageConfig(cfg);
      const patch: Record<string, unknown> = {};
      if (data.hero_image_url !== undefined) patch.hero_image_url = data.hero_image_url;
      if (data.tagline !== undefined) patch.tagline = data.tagline;
      if (data.page) {
        patch.map_config = {
          ...cfg,
          page: {
            sections: { ...prev.sections, ...(data.page.sections ?? {}) },
            lunch_ids: data.page.lunch_ids ?? prev.lunch_ids,
            event_ids: data.page.event_ids ?? prev.event_ids,
            restaurant_ids: data.page.restaurant_ids ?? prev.restaurant_ids,
            do_ids: data.page.do_ids ?? prev.do_ids,
            order: data.page.order ? normalizeSectionOrder(data.page.order) : prev.order,

          },
        };
      }
      if (Object.keys(patch).length === 0) continue;
      const { error } = await supabaseAdmin.from("districts").update(patch as never).eq("id", row.id);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const searchTrainFactoryPlaces = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        query: z.string().max(80).default(""),
        kind: z.enum(["lunch", "event", "restaurant", "do"]),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { assertIsAdmin } = await import("@/lib/assert-admin.server");
    await assertIsAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const term = data.query.replace(/[%_\\]/g, "").trim();
    let q = supabaseAdmin
      .from("places")
      .select("id,name,slug,category,section,image_url,starts_at,date_label,published")
      .eq("published", true)
      .order("name", { ascending: true })
      .limit(25);
    if (data.kind === "event") q = q.eq("section", "happening");
    else if (data.kind === "do") q = q.eq("section", "do");
    else if (data.kind === "restaurant") q = q.eq("section", "eat");
    else q = q.neq("section", "happening");
    if (term) q = q.ilike("name", `%${term}%`);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r: any) => ({
      id: r.id as string,
      name: r.name as string,
      slug: (r.slug ?? null) as string | null,
      category: (r.category ?? null) as string | null,
      section: r.section as string,
      image: (r.image_url ?? null) as string | null,
      starts_at: (r.starts_at ?? null) as string | null,
      date_label: (r.date_label ?? null) as string | null,
    }));
  });

const hhmm = z
  .string()
  .transform((s) => s.trim().slice(0, 5))
  .refine((s) => /^([01]\d|2[0-3]):[0-5]\d$/.test(s), "Use 24h time, e.g. 11:00");

function weeklyHours(open: string, close: string) {
  const day = { open, close };
  return { Mon: day, Tue: day, Wed: day, Thu: day, Fri: day, Sat: day, Sun: day };
}

function uniqueSlug(base: string) {
  return (
    base
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "place"
  );
}

/** Create a restaurant, event or activity and pin it on the Train Factory hub. */
export const createTrainFactoryPlace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        kind: z.enum(["restaurant", "event", "do"]),
        name: z.string().min(2).max(200),
        name_fi: z.string().max(200).optional(),
        category: z.string().min(1).max(80),
        address: z.string().max(300).nullish(),
        image_url: z.string().max(2000).nullish(),
        open: hhmm.optional(),
        close: hhmm.optional(),
        lunch_open: hhmm.optional(),
        lunch_close: hhmm.optional(),
        starts_at: z.string().max(40).nullish(),
        ends_at: z.string().max(40).nullish(),
        price: z.string().max(80).nullish(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { assertIsAdmin } = await import("@/lib/assert-admin.server");
    await assertIsAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const section = data.kind === "event" ? "happening" : data.kind === "do" ? "do" : "eat";
    const base = uniqueSlug(data.name);
    let slug = base;
    for (let i = 2; i < 60; i++) {
      const { data: hit } = await supabaseAdmin.from("places").select("id").eq("slug", slug).maybeSingle();
      if (!hit) break;
      slug = `${base}-${i}`;
    }

    const opening_hours = data.open && data.close ? weeklyHours(data.open, data.close) : null;
    const lunch_hours = data.lunch_open && data.lunch_close ? weeklyHours(data.lunch_open, data.lunch_close) : null;

    let date_label: string | null = null;
    if (data.kind === "event" && data.starts_at) {
      const s = new Date(data.starts_at);
      if (!Number.isNaN(s.getTime())) {
        date_label = s.toLocaleDateString("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      }
    }

    const { data: place, error } = await supabaseAdmin
      .from("places")
      .insert({
        slug,
        name: data.name.trim(),
        city_slug: "helsinki",
        section,
        category: data.category.trim(),
        description: "",
        address: data.address?.trim() || "Train Factory, Aleksis Kiven katu, 00510 Helsinki",
        image_url: data.image_url || null,
        gallery_urls: data.image_url ? [data.image_url] : [],
        opening_hours,
        lunch_hours,
        starts_at: data.starts_at || null,
        ends_at: data.ends_at || null,
        date_label,
        price: data.price?.trim() || null,
        published: true,
        tags: ["train-factory"],
      } as never)
      .select("id,name,slug,section,category,image_url")
      .single();
    if (error) throw new Error(error.message);

    const nameFi = data.name_fi?.trim();
    if (nameFi) {
      const { hashSource } = await import("@/lib/translations.server");
      const now = new Date().toISOString();
      const { error: trErr } = await supabaseAdmin.from("content_translations").upsert(
        {
          entity: "place",
          entity_id: place.id,
          field: "name",
          lang: "fi",
          value: nameFi,
          is_manual: true,
          review_status: "approved",
          reviewed_at: now,
          reviewed_by: userId,
          source_hash: hashSource(data.name.trim()),
          updated_at: now,
        } as never,
        { onConflict: "entity,entity_id,field,lang" },
      );
      if (trErr) throw new Error(trErr.message);
    }

    const { data: districts } = await supabaseAdmin
      .from("districts")
      .select("id,map_config")
      .in("slug", ["train-factory", "konepaja"]);

    for (const row of districts ?? []) {
      await supabaseAdmin.from("district_venues").upsert(
        { district_id: row.id, place_id: place.id },
        { onConflict: "district_id,place_id" },
      );
      const cfg =
        row.map_config && typeof row.map_config === "object" && !Array.isArray(row.map_config)
          ? (row.map_config as Record<string, unknown>)
          : {};
      const prev = parsePageConfig(cfg);
      const next = { ...prev };
      if (data.kind === "event") next.event_ids = [...prev.event_ids.filter((id) => id !== place.id), place.id];
      else if (data.kind === "do") next.do_ids = [...prev.do_ids.filter((id) => id !== place.id), place.id];
      else {
        next.restaurant_ids = [...prev.restaurant_ids.filter((id) => id !== place.id), place.id];
        if (lunch_hours) next.lunch_ids = [...prev.lunch_ids.filter((id) => id !== place.id), place.id];
      }
      const { error: cfgErr } = await supabaseAdmin
        .from("districts")
        .update({ map_config: { ...cfg, page: next } } as never)
        .eq("id", row.id);
      if (cfgErr) throw new Error(cfgErr.message);
    }

    return {
      id: place.id as string,
      name: place.name as string,
      slug: place.slug as string,
      section: place.section as string,
      category: (place.category ?? null) as string | null,
      image: (place.image_url ?? null) as string | null,
    };
  });

export const setDistrictVenue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        districtId: z.string().uuid(),
        placeId: z.string().uuid(),
        action: z.enum(["add", "remove", "feature", "unfeature"]),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await requireMaster(supabase, userId, data.districtId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    if (data.action === "remove") {
      const { error } = await supabaseAdmin
        .from("district_venues")
        .delete()
        .eq("district_id", data.districtId)
        .eq("place_id", data.placeId);
      if (error) throw new Error(error.message);
      return { ok: true };
    }
    if (data.action === "add") {
      const { error } = await supabaseAdmin
        .from("district_venues")
        .upsert({ district_id: data.districtId, place_id: data.placeId }, { onConflict: "district_id,place_id" });
      if (error) throw new Error(error.message);
      return { ok: true };
    }
    const { error } = await supabaseAdmin
      .from("district_venues")
      .update({ featured: data.action === "feature" })
      .eq("district_id", data.districtId)
      .eq("place_id", data.placeId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const searchPlacesForDistrict = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ districtId: z.string().uuid(), query: z.string().max(80).default("") }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await requireMaster(supabase, userId, data.districtId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const term = data.query.replace(/[%_\\]/g, "");
    let q = supabaseAdmin.from("places").select("id,name,category,section,city_slug,image_url").limit(20);
    if (term) q = q.ilike("name", `%${term}%`);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return (rows ?? []).map((r: any) => ({
      id: r.id as string,
      name: r.name as string,
      category: (r.category ?? null) as string | null,
      section: r.section as string,
      city_slug: (r.city_slug ?? null) as string | null,
      image: (r.image_url ?? null) as string | null,
    }));
  });

// ---------------------------------------------------------------------------
// Stale-content reminders (district master)
// ---------------------------------------------------------------------------

export type StaleVenueDTO = {
  id: string;
  name: string;
  email: string | null;
  info_updated_at: string | null;
  menu_updated_at: string | null;
  days_stale: number | null;
};

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.floor(ms / 86_400_000));
}

/** Venues in a district whose hours/menus haven't been touched recently. */
export const listStaleDistrictVenues = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z.object({ districtId: z.string().uuid(), thresholdDays: z.number().int().min(1).max(365).default(30) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await requireMaster(supabase, userId, data.districtId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: links } = await supabaseAdmin
      .from("district_venues")
      .select("place_id")
      .eq("district_id", data.districtId);
    const ids = (links ?? []).map((l: any) => l.place_id);
    if (ids.length === 0) return { venues: [] as StaleVenueDTO[], thresholdDays: data.thresholdDays };

    const { data: rows, error } = await supabaseAdmin
      .from("places")
      .select("id,name,email,info_updated_at,menu_updated_at")
      .in("id", ids);
    if (error) throw new Error(error.message);

    const venues = (rows ?? [])
      .map((p: any): StaleVenueDTO => {
        const latest = [p.info_updated_at, p.menu_updated_at]
          .filter(Boolean)
          .sort()
          .pop() as string | undefined;
        return {
          id: p.id,
          name: p.name,
          email: p.email ?? null,
          info_updated_at: p.info_updated_at ?? null,
          menu_updated_at: p.menu_updated_at ?? null,
          days_stale: daysSince(latest ?? null),
        };
      })
      .filter((v) => v.days_stale === null || v.days_stale >= data.thresholdDays)
      .sort((a, b) => (b.days_stale ?? 99_999) - (a.days_stale ?? 99_999));

    return { venues, thresholdDays: data.thresholdDays };
  });

/** Email a nudge to selected venues asking them to refresh hours/menus. */
export const sendVenueUpdateReminders = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input) =>
    z
      .object({
        districtId: z.string().uuid(),
        placeIds: z.array(z.string().uuid()).min(1).max(50),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    await requireMaster(supabase, userId, data.districtId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { sendVenueUpdateReminderEmail } = await import("@/lib/district-reminders.server");

    const { data: district } = await supabaseAdmin
      .from("districts")
      .select("name")
      .eq("id", data.districtId)
      .maybeSingle();

    const { data: links } = await supabaseAdmin
      .from("district_venues")
      .select("place_id")
      .eq("district_id", data.districtId)
      .in("place_id", data.placeIds);
    const allowed = new Set((links ?? []).map((l: any) => l.place_id as string));

    const { data: rows } = await supabaseAdmin
      .from("places")
      .select("id,name,email,info_updated_at,menu_updated_at")
      .in("id", Array.from(allowed));

    let sent = 0;
    const skipped: string[] = [];
    for (const p of (rows ?? []) as any[]) {
      if (!p.email) {
        skipped.push(p.name);
        continue;
      }
      const latest = [p.info_updated_at, p.menu_updated_at].filter(Boolean).sort().pop() as string | undefined;
      const res = await sendVenueUpdateReminderEmail({
        to: p.email,
        venueName: p.name,
        districtName: ((district as any)?.name ?? "the district") as string,
        daysStale: daysSince(latest ?? null),
      });
      if (res.sent) sent += 1;
      else skipped.push(p.name);
    }

    return { ok: true, sent, skipped };
  });

/** Admin district CMS: every district that can be edited. */
export const listDistrictsForAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { assertIsAdmin } = await import("@/lib/assert-admin.server");
    await assertIsAdmin(supabase, userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("districts")
      .select("id,slug,name,published")
      .order("name", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? [])
      .filter((d) => d.slug !== "konepaja")
      .map((d) => ({
        id: d.id as string,
        slug: d.slug as string,
        name: d.name as string,
        published: !!d.published,
      }));
  });
